import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, FileText, CheckCircle, Clock, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DocumentDetail() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [doc, setDoc] = useState(null);
  const [acks, setAcks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => { loadDoc(); }, [documentId]);

  const loadDoc = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/documents/${documentId}`, { headers });
      setDoc(res.data);

      // Try to load acknowledgements (admin view)
      try {
        const acksRes = await axios.get(`${API}/api/documents/${documentId}/acknowledgements`, { headers });
        setAcks(acksRes.data);
      } catch { /* not authorized = not admin */ }
    } catch {
      toast.error('Document not found');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/documents/${documentId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document acknowledged');
      loadDoc();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setAcknowledging(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  if (!doc) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Document</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Document header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#F0F4FF', color: '#3B82F6' }}>
              {doc.document_type?.replace('_', ' ')}
            </span>
            {doc.requires_acknowledgement && (
              <span className="px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                Acknowledgement required
              </span>
            )}
          </div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--fy-text-primary)' }} data-testid="doc-title">{doc.title}</h2>
          <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
            Created by {doc.created_by_name || 'Admin'} on {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}
          </p>
        </div>

        {/* Document content */}
        {doc.content && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--fy-text-primary)' }} data-testid="doc-content">
              {doc.content}
            </div>
          </Card>
        )}

        {/* Acknowledgement action */}
        {doc.requires_acknowledgement && user && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            {doc.acknowledged ? (
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--lk-sage)' }}>Acknowledged</p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                    {doc.acknowledged_at ? new Date(doc.acknowledged_at).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm mb-3" style={{ color: 'var(--fy-text-primary)' }}>
                  By acknowledging, you confirm you have read and understood this document.
                </p>
                <Button
                  onClick={handleAcknowledge}
                  disabled={acknowledging}
                  className="w-full rounded-full"
                  style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                  data-testid="acknowledge-btn"
                >
                  {acknowledging ? 'Processing...' : 'I Acknowledge'}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Admin: Acknowledgement review */}
        {acks && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
                Acknowledgement Review ({acks.total_acknowledged})
              </p>
            </div>
            {acks.acknowledgements.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>No acknowledgements yet</p>
            ) : (
              <div className="space-y-2">
                {acks.acknowledgements.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1" style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}>
                    <span style={{ color: 'var(--fy-text-primary)' }}>{a.user_name || 'Member'}</span>
                    <span className="flex items-center gap-1" style={{ color: 'var(--fy-text-disabled)' }}>
                      <Clock className="w-3 h-3" />
                      {a.acknowledged_at ? new Date(a.acknowledged_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
